import type { ReactDatePickerCustomHeaderProps, ReactDatePickerProps } from 'react-datepicker';
import type { DefaultValueType as I18nType } from '@whizz/react-i18n/dist/@types/context';
import React from 'react';
import ReactDatePicker from 'react-datepicker';
import { useI18n } from '@whizz/react-i18n';
import { DatepickerWrapper } from './Datepicker.styled';
import { DATEPICKER_WRAPPER } from '../../util/data-id';
import MaterialIcon from '../icon/Icon.material';
import { BUDDHIST_YEARS } from '../../util/vars';
import classNames from 'classnames';

interface Props extends ReactDatePickerProps {
  hideInputs?: boolean;
  startDate: Date | null;
  endDate: Date | null;
  maxDate: Date | null | undefined;
}

type CustomHeaderProps = Pick<I18nType, 'locale' | 'translate'> & Pick<ReactDatePickerProps, 'yearItemNumber' | 'maxDate' | 'showMonthDropdown' | 'showYearDropdown'>;

const DateRangePicker = ({
  hideInputs = false,
  startDate,
  endDate,
  maxDate,
  yearItemNumber,
  showMonthDropdown,
  showYearDropdown,
  ...other
}: Props): JSX.Element => {

  const { locale, translate } = useI18n();

  return (
    <DatepickerWrapper data-id={DATEPICKER_WRAPPER}>
      <ReactDatePicker
        inline={hideInputs}
        selected={startDate}
        startDate={startDate}
        endDate={endDate}
        locale={locale?.id}
        maxDate={maxDate}
        renderCustomHeader={CustomHeader({ locale, translate, maxDate, yearItemNumber, showMonthDropdown, showYearDropdown })}
        {...other as ReactDatePickerProps}
      />
    </DatepickerWrapper>
  );
};

const createYears = (to = new Date().getFullYear(), count = 10) => {
  const years = [];
  for (let x = to - count; x <= to; x++) {
    years.push(x);
  }
  return years;
};
const months = [
  'JANUARY',
  'FEBRUARY',
  'MARCH',
  'APRIL',
  'MAY',
  'JUNE',
  'JULY',
  'AUGUST',
  'SEPTEMBER',
  'OCTOBER',
  'NOVEMBER',
  'DECEMBER',
];

const CustomHeader = ({ locale, translate, maxDate, yearItemNumber, showMonthDropdown, showYearDropdown }: CustomHeaderProps) => ({
  date,
  changeYear,
  changeMonth,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}: ReactDatePickerCustomHeaderProps) => {
  const years = createYears(maxDate?.getFullYear(), yearItemNumber);
  const formatYear = (year: number) => locale?.id === 'th-th' ? year + BUDDHIST_YEARS : year;
  const formatMonth = (month: string) => translate(`months.${month}`);
  return (
    <div className={'flex justify-center gap-2 px-4'}>
      <button type={'button'} className={`px-2 border-2 border-primary rounded-default bg-white disabled:opacity-30 disabled:bg-gray-200 ${classNames({ 'cursor-pointer': !prevMonthButtonDisabled, 'cursor-default': prevMonthButtonDisabled })}`} onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
        <MaterialIcon icon={'ArrowBackIos'} size={'sm'} />
      </button>
      <select
        className={`rounded-default border-2 border-primary text-sm md:text-base text-primary ${classNames({ 'bg-none px-2 cursor-default opacity-100': !showMonthDropdown })}`}
        value={months[date.getMonth()]}
        onChange={({ target: { value } }) => changeMonth(months.indexOf(value))}
        disabled={!showMonthDropdown}
      >
        {months.map((option) => (
          <option key={option} value={option}>
            {formatMonth(option)}
          </option>
        ))}
      </select>

      <select
        className={`rounded-default border-2 border-primary text-sm md:text-base text-primary ${classNames({ 'bg-none px-2 cursor-default opacity-100': !showYearDropdown })}`}
        value={date.getFullYear()}
        onChange={({ target: { value } }) => changeYear(Number(value))}
        disabled={!showYearDropdown}
      >
        {years.map((option) => (
          <option key={option} value={option}>
            {formatYear(option)}
          </option>
        ))}
      </select>

      <button type={'button'} className={`px-2 border-2 border-primary rounded-default bg-white disabled:opacity-30 disabled:bg-gray-200 ${classNames({ 'cursor-pointer': !nextMonthButtonDisabled, 'cursor-default': nextMonthButtonDisabled })}`} onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
        <MaterialIcon icon={'ArrowForwardIos'} size={'sm'} />
      </button>
    </div>
  );
};

DateRangePicker.displayName = 'DateRangePicker';

export default DateRangePicker;
